// PlansPropsPanel.tsx — right properties panel for Plans mode
// Shows properties/actions for the active plan tab/item

import type { PlansData, RoofPlan, RoofElement, RoofElementType, Elevation, PanelData, DrawingSheet } from '../types/panelvar';
import { computeFootprint, computeRoofOutline } from '../utils/buildingFootprint';
import { computeRoofStructure } from '../utils/roofStructure';

interface Props {
  plans: PlansData | null;
  activeTab: string;
  onUpdatePlans: (patch: Partial<PlansData>) => void;
  elevations?: Elevation[];
  panels?: PanelData[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function PlansPropsPanel({ plans, activeTab, onUpdatePlans, elevations, panels, collapsed, onToggleCollapse }: Props) {
  if (collapsed) {
    return (
      <div className="building-props collapsed">
        <button className="collapse-toggle" onClick={onToggleCollapse} title="Expand properties">◀</button>
      </div>
    );
  }

  function addRoofElement(roofId: string, type: RoofElementType) {
    if (!plans) return;
    const roofPlans = plans.roofPlans.map(rp => {
      if (rp.id !== roofId) return rp;
      const newEl: RoofElement = {
        id: crypto.randomUUID(),
        type,
        name: type === 'dome' ? 'New Dome' : type === 'gable' ? 'New Gable' : type === 'barrel-vault' ? 'New Barrel Vault' : 'New Flat Section',
        x: 300,
        y: 300,
        diameter: type === 'dome' ? 360 : type === 'barrel-vault' ? 240 : undefined,
        length: type === 'barrel-vault' ? 480 : type === 'flat' ? 360 : undefined,
        width: type === 'gable' ? 360 : type === 'flat' ? 360 : undefined,
        ridgeHeight: type === 'gable' ? 120 : undefined,
      };
      return { ...rp, elements: [...rp.elements, newEl] };
    });
    onUpdatePlans({ roofPlans });
  }

  function addRoofPlan() {
    if (!plans) return;
    const newPlan: RoofPlan = {
      id: crypto.randomUUID(),
      name: `Roof Plan ${plans.roofPlans.length + 1}`,
      elements: [],
      notes: '',
    };

    // Auto-generate roof outline from footprint if elevations are available
    if (elevations && panels && elevations.length > 0) {
      const footprint = computeFootprint(elevations, panels);
      const outline = computeRoofOutline(footprint);
      if (outline.length >= 3) {
        newPlan.outline = outline;
        newPlan.structuralData = computeRoofStructure(outline);
      }
    }

    onUpdatePlans({ roofPlans: [...plans.roofPlans, newPlan] });
  }

  // Active roof plan for element editing
  const activeRoofId = activeTab.startsWith('roof-') ? activeTab.replace('roof-', '') : null;
  const activeRoof = activeRoofId ? plans?.roofPlans.find(r => r.id === activeRoofId) : null;

  // Active floor plan for auto-generate toggle
  const activeFloorId = activeTab.startsWith('floor-') ? activeTab.replace('floor-', '') : null;
  const activeFloor = activeFloorId ? plans?.floorPlans.find(f => f.id === activeFloorId) : null;

  function toggleFloorAutoGenerate(floor: DrawingSheet) {
    if (!plans) return;
    const isCurrentlyAuto = floor.autoGenerate !== false;
    const floorPlans = plans.floorPlans.map(f => {
      if (f.id !== floor.id) return f;
      if (isCurrentlyAuto) {
        // Turning OFF: snapshot current footprint corners
        let frozenFootprint = f.frozenFootprint;
        if (elevations && panels && elevations.length > 0) {
          frozenFootprint = computeFootprint(elevations, panels).corners;
        }
        return { ...f, autoGenerate: false, frozenFootprint };
      }
      // Turning ON: clear frozen data, resume live generation
      const { frozenFootprint: _drop, ...rest } = f;
      return { ...rest, autoGenerate: true };
    });
    onUpdatePlans({ floorPlans });
  }

  function toggleRoofAutoGenerate(roof: RoofPlan) {
    if (!plans) return;
    const isCurrentlyAuto = roof.autoGenerate !== false;
    const roofPlans = plans.roofPlans.map(rp => {
      if (rp.id !== roof.id) return rp;
      if (isCurrentlyAuto) {
        // Turning OFF: snapshot current outline
        let outline = rp.outline;
        if (elevations && panels && elevations.length > 0) {
          const fp = computeFootprint(elevations, panels);
          const computed = computeRoofOutline(fp);
          if (computed.length >= 3) outline = computed;
        }
        return { ...rp, autoGenerate: false, outline };
      }
      // Turning ON: clear frozen outline, resume live generation
      return { ...rp, autoGenerate: true, outline: undefined };
    });
    onUpdatePlans({ roofPlans });
  }

  return (
    <div className="building-props">
      <div className="building-props-header">
        <span className="building-props-title">Properties</span>
        {onToggleCollapse && (
          <button className="collapse-toggle" onClick={onToggleCollapse} title="Collapse properties">▶</button>
        )}
      </div>

      <div className="building-props-body">
        {activeTab === 'roofs' && (
          <div className="building-props-section">
            <h4>Roof Plans</h4>
            <button className="building-action-btn" onClick={addRoofPlan}>+ Add Roof Plan</button>
            {plans && plans.roofPlans.length > 0 && (
              <p className="building-props-hint">Select a roof plan from the sidebar to edit its elements.</p>
            )}
          </div>
        )}

        {activeRoof && (
          <div className="building-props-section">
            <h4>{activeRoof.name}</h4>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={activeRoof.autoGenerate !== false}
                onChange={() => toggleRoofAutoGenerate(activeRoof)}
              />
              Auto-generate from elevations
            </label>
            {activeRoof.autoGenerate === false && (
              <p className="building-props-hint" style={{ color: '#b68a00' }}>
                Plan is frozen. Uncheck to resume live generation from elevations.
              </p>
            )}
            <div className="building-add-elements">
              <span className="building-props-label">Add Element:</span>
              <button className="building-action-btn" onClick={() => addRoofElement(activeRoof.id, 'dome')}>Dome</button>
              <button className="building-action-btn" onClick={() => addRoofElement(activeRoof.id, 'gable')}>Gable</button>
              <button className="building-action-btn" onClick={() => addRoofElement(activeRoof.id, 'barrel-vault')}>Barrel Vault</button>
              <button className="building-action-btn" onClick={() => addRoofElement(activeRoof.id, 'flat')}>Flat</button>
            </div>
            {activeRoof.elements.length > 0 && (
              <div className="building-element-list">
                {activeRoof.elements.map(el => (
                  <div key={el.id} className="building-element-row">
                    <span className="building-element-type">{el.type}</span>
                    <span className="building-element-name">{el.name}</span>
                    {el.type === 'dome' && el.diameter && (
                      <span className="building-element-dim">⌀{Math.round(el.diameter / 12)}&apos;</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'overall' && (
          <div className="building-props-section">
            <h4>Overall Building Plan</h4>
            <p className="building-props-hint">The overall building plan shows the complete footprint with all elevations overlaid.</p>
          </div>
        )}

        {activeTab === 'foundation' && (
          <div className="building-props-section">
            <h4>Foundation Plan</h4>
            <p className="building-props-hint">Foundation plan shows footing locations, sizes, and reinforcement details.</p>
          </div>
        )}

        {activeTab === 'walls' && (
          <div className="building-props-section">
            <h4>Wall Sections</h4>
            <p className="building-props-hint">Wall sections (A through Z) show vertical cross-cuts through the building walls.</p>
          </div>
        )}

        {activeTab === 'footings' && (
          <div className="building-props-section">
            <h4>Footing Sections</h4>
            <p className="building-props-hint">Footing sections show detailed cross-section views of individual footings.</p>
          </div>
        )}

        {activeTab === 'doors' && (
          <div className="building-props-section">
            <h4>Door Types</h4>
            <p className="building-props-hint">Door type schedule showing dimensions and specifications for each door type.</p>
          </div>
        )}

        {activeTab === 'windows' && (
          <div className="building-props-section">
            <h4>Window Types</h4>
            <p className="building-props-hint">Window type schedule showing dimensions and specifications for each window type.</p>
          </div>
        )}

        {activeTab === 'floors' && (
          <div className="building-props-section">
            <h4>Floor Plans</h4>
            <p className="building-props-hint">Floor plans show panel layout at each level of the building.</p>
          </div>
        )}

        {activeFloor && (
          <div className="building-props-section">
            <h4>{activeFloor.name}</h4>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={activeFloor.autoGenerate !== false}
                onChange={() => toggleFloorAutoGenerate(activeFloor)}
              />
              Auto-generate from elevations
            </label>
            {activeFloor.autoGenerate === false && (
              <p className="building-props-hint" style={{ color: '#b68a00' }}>
                Plan is frozen. Uncheck to resume live generation from elevations.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
